import { AttachmentFile } from '@nextforce/api'
import { Icon, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Platform, StyleSheet, View, ViewProps } from 'react-native'
import { createElement } from 'react-native-web'
import { getDarkShadeColor, getLightShadeColor } from 'shared/utils'
import { handleAttachmentPaste } from './chat/custom_text_input.web'

interface DragDropProps extends ViewProps {
  children: React.ReactNode
  onDragEnter?: (e: React.DragEvent<HTMLElement>) => void
  onDragLeave?: (e: React.DragEvent<HTMLElement>) => void
  onDragOver?: (e: React.DragEvent<HTMLElement>) => void
  onDrop?: (e: React.DragEvent<HTMLElement>) => void
}

const DragDropView = (props: DragDropProps): React.JSX.Element => {
  if (Platform.OS === 'web') {
    return createElement('div', props)
  } else {
    return <View {...props} />
  }
}

interface DragDropZoneProps {
  style?: ViewProps['style']
  onDrop: (files: AttachmentFile[]) => void
  onDropUrl: (url: string) => void
  children: React.ReactNode
}

export const DragDropZone = ({
  onDrop,
  onDropUrl,
  style,
  children,
}: DragDropZoneProps): React.ReactElement<DragDropProps> => {
  const [dragActive, setDragActive] = useState(false)
  const dragCounter = useRef(0)
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

  const debouncedSetDragActive = useCallback((value: boolean) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }
    debounceTimeout.current = setTimeout(() => {
      setDragActive(value)
    }, 50)
  }, [])

  const handleDrag = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()

      if (e.type === 'dragenter') {
        dragCounter.current++
        if (dragCounter.current === 1) {
          debouncedSetDragActive(true)
        }
      } else if (e.type === 'dragleave') {
        dragCounter.current--
        if (dragCounter.current === 0) {
          debouncedSetDragActive(false)
        }
      }
    },
    [debouncedSetDragActive]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounter.current = 0
      setDragActive(false)

      let files: File[] = []
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        files = Array.from(e.dataTransfer.files)
      }
      if (files.length === 0) {
        const fileUrl = e.dataTransfer.getData('text/uri-list')
        if (fileUrl) {
          return onDropUrl(fileUrl)
        }
      }
      return handleAttachmentPaste(files, onDrop)
    },
    [onDrop, onDropUrl]
  )

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [])

  const styles = useStyles()
  return (
    <DragDropView
      style={[style, styles.dropzone]}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {dragActive ? <DragActiveModal /> : null}
      {children}
    </DragDropView>
  )
}

const DragActiveModal = () => {
  const styles = useStyles()
  return (
    <View style={styles.dragActiveModal}>
      <View style={styles.dragActiveModalContent}>
        <Text style={styles.modalText}>{t('dropFilesHere')}</Text>
        <Icon
          name={'upload'}
          type="ant-design"
          size={24}
          color={styles.modalText.color}
        />
      </View>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  dragActiveModal: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: getDarkShadeColor(0.7),
    zIndex: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dragActiveModalContent: {
    padding: 100,
    borderWidth: 3,
    borderColor:
      theme.mode === 'dark' ? getLightShadeColor(0.2) : getLightShadeColor(),
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: getDarkShadeColor(),
  },
  modalText: {
    marginBottom: 20,
    color: getLightShadeColor(),
  },
}))
